<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import webapp from '@/services/webapp'
import { initialize } from '@/composables/init'
import TheLayout from '@/TheLayout.vue'
import LoadingPage from '@/components/LoadingPage.vue'
import { TolgeeProvider } from '@tolgee/vue'
import { useAuth } from '@/composables/auth'
import { isBaseMiniApp } from '@/services/base'
import { useAnalytics } from '@/composables/analytics'
import { TonConnectUIProvider } from '@townsquarelabs/ui-vue'
import { env } from '@/env'
import { sleep } from '@/services/helper'
import { useNewLoader } from '@/config/new-loader'
import router from '@/router'
import { RouteName } from '@/routes'
import { useLocalStorage } from '@vueuse/core'
import { buildDropeeStorageKey } from '@/config/config'
import { currentTimestamp } from '@/helpers/date'
import { useFullScreenDimensions } from '@/composables/helpers/fullscreen'
import { useRoute } from 'vue-router'
import GameResultLoading from '@/components/GameResultLoading.vue'
import TempInBetweenScreen from '@/components/TempInBetweenScreen.vue'
import { sdk } from '@farcaster/miniapp-sdk'
import { useNow } from '@/composables/game/time'

const route = useRoute()
const isReturningFromMiniGame = computed(() => !!route.query.gameSubmitted)
const isInitialized = ref(false)
const { nowDateMinuteTimestamp } = useNow()
const endOfSeason = new Date('2026-09-01T15:30:00Z').getTime() / 1000
const { setUserId } = useAnalytics()
const { topNumber } = useFullScreenDimensions()

const lastInitTimestamp = useLocalStorage<number>(buildDropeeStorageKey('lastInitTimestamp'), 0)

const needsFullHeight = computed(() => Boolean(router.currentRoute.value.meta.fullHeight))

const showInBetweenScreen = computed(() => false)

const topStyles = computed(() => {
  return { paddingTop: isInitialized.value ? `${topNumber.value}px` : `0px` }
})

const shouldApplyTopStyles = computed(() => {
  const isBaseMiniAppHome = isBaseMiniApp && router.currentRoute.value.name === RouteName.HOME
  return isInitialized.value && !needsFullHeight.value && !showInBetweenScreen.value && !isBaseMiniAppHome
})

async function _initialize() {
  if (!useNewLoader) {
    await initialize()
  }
  const { profile } = useAuth()
  setUserId(String(profile.value.id))
}

onMounted(async () => {
  try {
    webapp.requestFullscreen()
  } catch {
    await webapp.showAlert('Please update Telegram')
  }
  webapp.expand()
  webapp.ready()
  await sdk.actions.ready({ disableNativeGestures: true })
  // // @ts-ignore
  // window.TelegramAdsController = new TelegramAdsController()
  // // @ts-ignore
  // window.TelegramAdsController.initialize({
  //   pubId: '968536',
  //   appId: '2054',
  // })

  // Skip loading screen if returning from game
  if (isReturningFromMiniGame.value) {
    await _initialize()
    lastInitTimestamp.value = currentTimestamp()
    isInitialized.value = true
  } else {
    await Promise.all([
      _initialize(),
      sleep(3000),
    ])
    lastInitTimestamp.value = currentTimestamp()
    isInitialized.value = true
  }
  await sdk.actions.addMiniApp()
})

function preventCollapse() {
  if (window.scrollY === 0) {
    window.scrollTo(0, 1)
  }
}

onMounted(() => {
  const adSonar = document.createElement('script')
  adSonar.setAttribute('src', 'https://static.sonartech.io/lib/1.0.0/sonar.js?appId=app_8a8db56d')
  document.head.appendChild(adSonar)
})

const manifestUrl = computed(() => `${window.location.origin}/tonconnect-manifest${env.app.isProd ? '' : '-dev'}.json`)
</script>

<template>
  <TonConnectUIProvider
    :options="{ manifestUrl: manifestUrl }"
  >
    <TolgeeProvider>
      <div class="app-bg text-white max-w-[1023px] my-0 mx-auto w-screen h-screen outer-scroll-container">
        <div class="overflow-hidden w-full h-screen outer-scroll-container max-w-[440px] mx-auto">
          <div
            id="app-wrapper"
            @touchstart="preventCollapse"
            class="w-full scroll-container outer-scroll-container flex flex-col h-screen"
            :class="router.currentRoute.value.name !== RouteName.HOME && 'overflow-auto'"
            :style="shouldApplyTopStyles ? topStyles : undefined"
          >
            <div id="adsonar_all" />
            <!-- <TheQRCode
              v-if="webapp.isNotMobile"
              class="text-black"
            /> -->
            <TempInBetweenScreen v-if="showInBetweenScreen" />
            <template v-else-if="!isInitialized">
              <LoadingPage v-if="!isReturningFromMiniGame" />
              <GameResultLoading v-else />
            </template>
            <TheLayout
              v-else
            />
          </div>
        </div>
      </div>
    </TolgeeProvider>
  </TonConnectUIProvider>
</template>

<style scoped>
.app-bg {
	background-image: url('@/assets/images/app-background.webp');
	background-size: cover;
	background-repeat: no-repeat;
	background-blend-mode: overlay;
}

.outer-scroll-container {
	overscroll-behavior: none;
}
</style>
